'use client'

import { motion, type Transition, type Variants } from 'framer-motion'
import type { ComponentType, ReactNode } from 'react'

import { cn } from '@/lib/utils'

// The prototype's springs (orderx-prototype src/components/motion.tsx): `spring` for things that
// slide under the pointer (tabs, fills), `softSpring` for cards settling into place.
export const HOME_SPRING: Transition = { type: 'spring', stiffness: 520, damping: 38, mass: 0.7 }
export const HOME_SOFT_SPRING: Transition = { type: 'spring', stiffness: 320, damping: 32 }
export const HOME_EASE = [0.2, 0.8, 0.2, 1] as const

/** The page container: its sections come in one after another, top to bottom. */
export const HOME_STAGGER: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
}

/** One section's entrance — rises a little and settles, like a widget placed on the grid. */
export const HOME_REVEAL: Variants = {
  hidden: { opacity: 0, y: 14, scale: 0.985 },
  show: { opacity: 1, y: 0, scale: 1, transition: HOME_SOFT_SPRING },
}

/** A list inside a section: rows follow each other in once the section has landed. */
export const HOME_LIST: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.035, delayChildren: 0.12 } },
}

export const HOME_ROW: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: HOME_EASE } },
}

interface HomePanelProps {
  title: ReactNode
  icon?: ComponentType<{ className?: string }>
  /** Right side of the header: a link, a tab switcher, a status pill. */
  aside?: ReactNode
  className?: string
  bodyClassName?: string
  children: ReactNode
  /** For in-page anchors and the welcome tour. */
  id?: string
}

/**
 * A Home section: the widget frame (Card surface, hairline, --widget-radius) with a header row,
 * revealed by the page's stagger. Sections sit straight on the page gradient like widgets do.
 */
export function HomePanel({
  title,
  icon: Icon,
  aside,
  className,
  bodyClassName,
  children,
  id,
}: HomePanelProps) {
  return (
    <motion.section
      id={id}
      variants={HOME_REVEAL}
      className={cn(
        'bg-card border-foreground/10 text-card-foreground flex min-w-0 flex-col overflow-hidden rounded-(--widget-radius) border',
        className
      )}
    >
      {/* Wraps on a narrow screen: the switchers drop under the title instead of overflowing. */}
      <header className="flex min-h-11 shrink-0 flex-wrap items-center gap-x-2 gap-y-1.5 px-4 py-2">
        {Icon && <Icon className="text-foreground/50 size-4 shrink-0" />}
        <h2 className="text-foreground min-w-0 truncate text-sm font-medium tracking-tight">
          {title}
        </h2>
        {aside && (
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
            {aside}
          </div>
        )}
      </header>
      <div className={cn('flex min-h-0 flex-1 flex-col px-4 pb-4', bodyClassName)}>{children}</div>
    </motion.section>
  )
}

/** The quiet "View all →" style link in a section header. */
export function HomeHeaderLink({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-foreground/50 hover:text-foreground flex cursor-pointer items-center gap-1 text-xs tracking-tight whitespace-nowrap transition-colors duration-150"
    >
      {children}
      <span
        aria-hidden="true"
        className="transition-transform duration-200 ease-(--ease-spring) group-hover:translate-x-0.5"
      >
        →
      </span>
    </button>
  )
}

/** A thin bar that grows to `value` (0–1) with the soft spring whenever it changes. */
export function HomeMeter({
  value,
  className,
  barClassName,
}: {
  value: number
  className?: string
  barClassName?: string
}) {
  const clamped = Math.min(1, Math.max(0, value))
  return (
    <div className={cn('bg-foreground/10 h-1 overflow-hidden rounded-full', className)}>
      <motion.div
        className={cn('bg-foreground h-full origin-left rounded-full', barClassName)}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: clamped }}
        transition={HOME_SOFT_SPRING}
      />
    </div>
  )
}

/** Pulsing live dot (the status bar's connection pulse). */
export function LiveDot({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'size-1.5 shrink-0 rounded-full bg-current motion-safe:animate-[conn-pulse_2s_infinite]',
        className
      )}
    />
  )
}
